import { useState } from "react";
import { requestPasswordReset } from "../library/allauth";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import XRoadz_Logo from "../img/XRoadz_Logo.png";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      await requestPasswordReset(email);
      setDone(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        .a1 { animation: fadeUp 0.5s ease forwards; }
        .a2 { animation: fadeUp 0.5s ease 0.15s forwards; opacity:0; }
      `}</style>

      <div className="min-h-screen flex">
        {/* Left Panel */}
        <div className="hidden lg:flex w-1/2 flex-col justify-between p-14 relative overflow-hidden bg-emerald-700">
          <div className="absolute w-[500px] h-[500px] rounded-full bg-emerald-600 opacity-20 -top-32 -left-32" />
          <div className="absolute w-[300px] h-[300px] rounded-full bg-emerald-900 opacity-20 bottom-20 right-0" />

          <div className="relative z-10 flex items-center gap-4 mt-6">
            <div className="flex items-center gap-4 mt-6">
              <div className="h-12 bg-white rounded-xl flex items-center justify-center shadow-sm px-3 overflow-hidden">
                <img
                  src={XRoadz_Logo}
                  alt="XRoadz"
                  className="h-7 object-contain"
                />
              </div>
              <div>
                <p className="text-white font-bold text-xl leading-tight">
                  XRoadz WebPortal
                </p>
                <p className="text-emerald-200 text-xs opacity-70 tracking-widest uppercase">
                  Traffic Analytics & Roadway Safety
                </p>
              </div>
            </div>
          </div>

          <div className="relative z-10 max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-yellow-300 border-opacity-40 mb-6">
              <span className="w-2 h-2 rounded-full bg-yellow-300 animate-pulse" />
              <span className="text-yellow-200 text-xs tracking-widest uppercase font-medium">
                Secure Account Recovery
              </span>
            </div>
            <h1 className="text-white text-5xl font-black leading-tight mb-6 tracking-tight">
              Reset your
              <br />
              <span className="text-emerald-200">password</span> securely.
            </h1>
            <p className="text-emerald-100 text-sm leading-relaxed opacity-80">
              Enter your registered email address and we will send you a secure
              link to reset your password.
            </p>
          </div>

          <div className="relative z-10">
            <p className="text-emerald-200 text-xs tracking-widest uppercase opacity-50">
              © {new Date().getFullYear()} XRoadz Inc. — Innovating Traffic
              Safety
            </p>
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 px-8 py-12">
          <div className="w-full max-w-lg">
            <div className="flex lg:hidden justify-center mb-8">
              <img
                src={XRoadz_Logo}
                alt="XRoadz"
                className="h-10 object-contain"
              />
            </div>

            {done ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 shadow-sm text-center a1">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={32} className="text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Check your email
                </h2>
                <p className="text-sm text-gray-400 mb-6">
                  If an account exists for <strong>{email}</strong>, you will
                  receive a reset link shortly.
                </p>
                <a
                  href="/login"
                  className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-800 font-medium"
                >
                  <ArrowLeft size={14} /> Back to Login
                </a>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 shadow-sm">
                <div className="mb-8 a1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    Forgot your password?
                  </h2>
                  <p className="text-sm text-gray-400">
                    Enter your email and we will send you a reset link.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5 a2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Email address
                    </label>
                    <div className="relative">
                      <Mail
                        size={15}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email address"
                        className="w-full pl-9 pr-4 py-3 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-gray-400 transition-colors"
                      />
                    </div>
                  </div>

                  {error && <p className="text-sm text-red-500">{error}</p>}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 text-sm font-semibold text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors disabled:opacity-60"
                  >
                    {loading ? "Sending..." : "Send Reset Link"}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <a
                    href="/login"
                    className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <ArrowLeft size={14} /> Back to Login
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
